import React, { FC, useEffect, useRef, useState } from "react";
import {
    Badge,
    Button,
    Card,
    Col,
    Collapse,
    FormControl,
    InputGroup,
    ListGroup,
    ListGroupItem,
    Row
} from "react-bootstrap";
import { AditorSceneData } from "../../../models/aditor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheck,
    faChevronDown,
    faChevronUp,
    faSave,
    faTrash
} from "@fortawesome/free-solid-svg-icons";
import {
    getDayAndMonthFromUnixTimestampMs,
    getFormattedTime,
    getUnixTimestampFromDateString,
    validateBuildSetName
} from "../../../common/Helpers";

interface Props {
    aditorScene: AditorSceneData;
    updateSceneDetails: (sceneId: number, tag: string, hash: string) => void;
    loadScene: () => void;
    deleteScene: () => void;
    currentSceneHash?: string;
    latestHash?: string;
    canAddTag?: boolean;
    focusAtStart?: boolean;
}

export const AditorHistoryElement: FC<Props> = ({
    aditorScene,
    updateSceneDetails,
    loadScene,
    deleteScene,
    currentSceneHash,
    latestHash,
    canAddTag,
    focusAtStart
}) => {
    const elementReference = useRef<HTMLDivElement | null>(null);
    const [showTagEdit, updateShowTagEdit] = useState<boolean>(false);
    const [showDetails, updateShowDetails] = useState(false);
    const [showDeleteConfirmation, updateShowDeleteConfirmation] =
        useState<boolean>(false);
    const [tagString, updateTagString] = useState<string>(
        aditorScene.tag || ""
    );

    let formattedDate = "";
    if (aditorScene.dateCreated) {
        formattedDate = getFormattedTime(aditorScene.dateCreated);
    }

    useEffect(() => {
        if (focusAtStart) {
            // @ts-ignore
            elementReference.current.scrollIntoView({ behavior: "smooth" });
            updateShowDetails(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Card className="mb-1">
            <Card.Header
                className="mouseHover"
                onClick={() => updateShowDetails(!showDetails)}
            >
                <Row>
                    <Col>
                        <div className="fw-bold">
                            {aditorScene.tag ? (
                                <>{aditorScene.tag}</>
                            ) : (
                                <>Save {aditorScene.id}</>
                            )}
                            <FontAwesomeIcon
                                size="xs"
                                className="ps-2"
                                icon={showDetails ? faChevronUp : faChevronDown}
                            />
                        </div>
                        <div className="medium-font pt-1">{formattedDate}</div>
                    </Col>
                    <Col xs="auto" className="d-flex align-items-center">
                        {currentSceneHash === aditorScene.hash && (
                            <Badge title="Currently open save">Current</Badge>
                        )}
                        {latestHash === aditorScene.hash && (
                            <Badge
                                title="Latest save used in build"
                                bg="success"
                                className={
                                    currentSceneHash === aditorScene.hash
                                        ? "ms-2"
                                        : ""
                                }
                            >
                                Latest
                            </Badge>
                        )}
                        {aditorScene.expirationDate && !aditorScene.tag ? (
                            <Badge
                                title={`Scheduled to be removed at ${getDayAndMonthFromUnixTimestampMs(getUnixTimestampFromDateString(aditorScene.expirationDate))}`}
                                bg="secondary"
                                className={
                                    currentSceneHash === aditorScene.hash ||
                                    latestHash === aditorScene.hash
                                        ? "ms-2"
                                        : ""
                                }
                            >
                                TBR
                            </Badge>
                        ) : null}
                    </Col>
                    <Col className="d-flex justify-content-end align-items-center">
                        <div>
                            {currentSceneHash !== aditorScene.hash ? (
                                <Button className="me-2" onClick={loadScene}>
                                    Load
                                </Button>
                            ) : null}
                            {!aditorScene.tag && canAddTag ? (
                                <Button
                                    variant="info"
                                    className="me-2"
                                    title="Store save"
                                    onClick={(
                                        e: React.MouseEvent<HTMLElement>
                                    ) => {
                                        updateShowTagEdit(!showTagEdit);
                                        e.stopPropagation();
                                    }}
                                >
                                    <FontAwesomeIcon icon={faSave} />
                                </Button>
                            ) : null}
                            <Button
                                variant="danger"
                                disabled={currentSceneHash === aditorScene.hash}
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                    e.stopPropagation();
                                    updateShowDeleteConfirmation(
                                        !showDeleteConfirmation
                                    );
                                }}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Card.Header>
            <Collapse in={showDetails}>
                <div>
                    <Card.Body ref={elementReference}>
                        <ListGroup className="medium-font">
                            <ListGroupItem
                                variant={!aditorScene.tag ? "info" : "primary"}
                            >
                                <b>S3 uuid:</b> {aditorScene.uuid}
                            </ListGroupItem>
                            <ListGroupItem
                                variant={!aditorScene.tag ? "info" : "primary"}
                            >
                                <b>Hash:</b> {aditorScene.hash}
                            </ListGroupItem>
                            <ListGroupItem
                                variant={!aditorScene.tag ? "info" : "primary"}
                            >
                                <b>Timestamp:</b> {aditorScene.dateCreated}
                            </ListGroupItem>
                            {aditorScene.expirationDate ? (
                                <ListGroupItem
                                    variant={
                                        !aditorScene.tag ? "info" : "primary"
                                    }
                                >
                                    <b>Expiration Date:</b>{" "}
                                    {aditorScene.expirationDate}
                                </ListGroupItem>
                            ) : null}
                            {aditorScene.user ? (
                                <ListGroupItem
                                    variant={
                                        !aditorScene.tag ? "info" : "primary"
                                    }
                                >
                                    <b>User:</b> {aditorScene.user.displayName}
                                </ListGroupItem>
                            ) : null}
                        </ListGroup>
                    </Card.Body>
                </div>
            </Collapse>

            <Collapse in={showTagEdit && !aditorScene.tag}>
                <div>
                    <Card.Footer className="d-flex justify-content-center">
                        <InputGroup style={{ maxWidth: "350px" }}>
                            <InputGroup.Text className="input-group-prepend">
                                Tag
                            </InputGroup.Text>
                            <FormControl
                                type="string"
                                placeholder="Give a tag to store the save"
                                value={tagString}
                                onChange={e => {
                                    const newValue = e.target.value.replace(
                                        " ",
                                        ""
                                    );
                                    if (validateBuildSetName(newValue, true)) {
                                        updateTagString(newValue);
                                    }
                                }}
                            />
                            <Button
                                variant="info"
                                disabled={tagString.length === 0}
                                onClick={() => {
                                    if (tagString.length > 0) {
                                        updateSceneDetails(
                                            aditorScene.id,
                                            tagString,
                                            aditorScene.hash
                                        );
                                        updateShowTagEdit(false);
                                    }
                                }}
                            >
                                <FontAwesomeIcon icon={faCheck} />
                            </Button>
                        </InputGroup>
                    </Card.Footer>
                </div>
            </Collapse>
            <Collapse
                in={
                    showDeleteConfirmation &&
                    currentSceneHash !== aditorScene.hash
                }
            >
                <div>
                    <Card.Footer>
                        <div className="d-flex justify-content-center">
                            <Button
                                className="me-1"
                                variant="secondary"
                                onClick={() => {
                                    updateShowDeleteConfirmation(false);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="ms-1"
                                variant="danger"
                                onClick={() => {
                                    updateShowDeleteConfirmation(false);
                                    deleteScene();
                                }}
                            >
                                Delete Save
                            </Button>
                        </div>
                    </Card.Footer>
                </div>
            </Collapse>
        </Card>
    );
};
